import { DownOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { Edit, useForm } from "@refinedev/antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { Button, Checkbox, Dropdown, Form, Image, Input, Menu, notification, Upload } from "antd";
import { useWatch } from "antd/es/form/Form";
import EditPhoneForm from "components/EditPhoneForm";
import EditLocationForm from "components/EditLocationForm";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateUserPhoneUseCase } from "usecases/user/updateUserPhoneUseCase";
import { updateUserProfileImage } from "usecases/user/updateUserProfileImageUseCase";
import { updateUserUsernameUseCase } from "usecases/user/updateUserUsernameUseCase";
import { updateUserVerifiedUseCase } from "usecases/user/updateUserVerifiedUseCase";
import { updateUserProfileUseCase } from "usecases/user/updateUserProfileUseCase";
import { UserUpdateDTO } from "../../interfaces";
import { UsernameValidator } from "../../services/user/UsernameValidator";
import { EmailValidator } from "../../services/user/EmailValidator";
import { Gender, GENDER_ITEMS } from "../../utils/constants";
import { BirthDateValidator } from "../../services/user/BirthDateValidator";
import { parseDDMMYYYY } from "../../utils/datetime";

const FILE_SIZE = 2 * 1024 * 1024;
export const UserEdit: React.FC<IResourceComponentsProps> = () => {
  const { formProps, form } = useForm<UserUpdateDTO>({
    redirect: "show",
    warnWhenUnsavedChanges: false,
    queryOptions: {
      retry: 0,
    },
  });
  const selectedGender = useWatch("gender", form);
  const navigate = useNavigate();

  if (!formProps?.initialValues?.id) {
    navigate("/users/find");
  }

  const genderMenu = (
    <Menu
      onClick={ (info) => {
        form.setFieldsValue({ gender: info.key });
      } }
      items={ GENDER_ITEMS }
    />
  );

  const [userId, setUserId] = useState(null);
  const [isFileFormatValid, setIsFileFormatValid] = React.useState(true);
  const [isFileSizeValid, setIsFileSizeValid] = React.useState(true);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);

  useEffect(() => {
    if (!userId) {
      setUserId(formProps?.initialValues?.id);
    }
  }, [userId, formProps.initialValues]);

  const handleFormSubmit = async (values: any) => {
    if (!isFileFormatValid) {
      notification.error({
        message: "Invalid file type",
        description: "You can only upload .jpg file!",
      });
      return false;
    }
    if (!isFileSizeValid) {
      notification.error({
        message: "Invalid size",
        description: "Please upload a file no larger than 2MB.",
      });
      return false;
    }
    if (uploadedFile) {
      form.setFieldsValue({
        image: uploadedFile,
      });
    }

    return true;
  };

  async function onFinish(values: any) {
    if (!userId) {
      throw new Error("User id not found");
    }
    try {
      if (formProps?.initialValues?.username !== values.username) {
        await updateUserUsernameUseCase(userId, { username: values.username });
        notification.success({
          message: "User username updated successfully",
        });
      }

      if (formProps?.initialValues?.verified !== values.verified) {
        await updateUserVerifiedUseCase(userId, {
          verified: values.verified,
        });
        notification.success({
          message: "User verified status updated successfully",
        });
      }

      if (values.image instanceof File) {
        await updateUserProfileImage(userId, values.image);
      }

      if (formProps?.initialValues?.phone !== values.phone) {
        await updateUserPhoneUseCase(userId, {
          phone: values.phone,
          isoCode: values.isoCode,
        });
        notification.success({
          message: "User phone updated successfully",
        });
      }
      const profileAttributed = {
        ...(formProps?.initialValues?.name !== values.name && { name: values.name }),
        ...(formProps?.initialValues?.email !== values.email && { email: values.email }),
        ...(formProps?.initialValues?.lastname !== values.lastname && { lastname: values.lastname }),
        ...(formProps?.initialValues?.country !== values.country && { country: values.country }),
        ...(formProps?.initialValues?.state !== values.state && { state: values.state }),
        ...(formProps?.initialValues?.city !== values.city && { city: values.city }),
        ...(formProps?.initialValues?.gender !== values.gender && { gender: values.gender }),
        ...(formProps?.initialValues?.birthday !== values.birthday && { birthday: parseDDMMYYYY(values.birthday).toDateString() }),
      }
      if (Object.keys(profileAttributed).length > 0) {
        await updateUserProfileUseCase(userId, profileAttributed);
        notification.success({
          message: "User details updated successfully",
        });
      }

      navigate("/users/show/" + values.username);
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.message,
        description: error?.response?.status,
      });
    }
  }

  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      canDelete={false}
      headerButtons={<></>}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={onFinish}
        onSubmitCapture={handleFormSubmit}
      >
        <Form.Item
          label="Username"
          name="username"
          rules={[
            {
              required: false,
              validator: async (_, value: string) => {
                const validator = new UsernameValidator();
                if (validator.validate(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(validator.errorMessage()));
              },
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={ [{ required: false }] }
          initialValue={ true }
        >
          <Input/>
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="lastname"
          rules={ [{ required: false }] }
          initialValue={ true }
        >
          <Input/>
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={ [
            {
              required: false,
              validator: async (_, value: string) => {
                const validator = new EmailValidator();
                if (validator.validate(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(validator.errorMessage()));
              },
            },
          ] }
          initialValue={ true }
        >
          <Input/>
        </Form.Item>

        <EditPhoneForm form={form} formProps={formProps} />

        <Form.Item
          label="Verified"
          name="verified"
          valuePropName="checked"
          rules={[
            {
              required: false,
            },
          ]}
          initialValue={true}
        >
          <Checkbox title={"Verified"} name="verified" defaultChecked={true} />
        </Form.Item>

        <Form.Item label="Gender" name="gender" rules={ [{ required: false }] }>
          <Dropdown
            overlay={
              <Menu
                onClick={ ({ key }) => {
                  form.setFieldsValue({ gender: key });
                } }
                items={ GENDER_ITEMS }
              />
            }
            trigger={ ["click"] }
          >
            <Button>
              { Gender[selectedGender as keyof typeof Gender] || "Gender" } <DownOutlined/>
            </Button>
          </Dropdown>
        </Form.Item>

        <Form.Item
          label="Birthday"
          name="birthday"
          rules={ [
            {
              required: false,
              validator: async (_, value: string) => {
                const validator = new BirthDateValidator();
                if (validator.validate(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(validator.errorMessage()));
              },
            },
          ] }
          initialValue={ true }
        >
          <Input/>
        </Form.Item>

        <EditLocationForm form={ form } formProps={ formProps }/>

        {/*<Form.Item*/}
        {/*  label="Sesh Role"*/}
        {/*  name="role"*/}
        {/*  rules={ [{*/}
        {/*    required: false,*/}
        {/*  }] }*/}
        {/*  initialValue={ true }*/}
        {/*>*/}
        {/*  <Dropdown overlay={ roleMenu }>*/}
        {/*    <Button>*/}
        {/*      { form.getFieldValue("role") || "Role" } <DownOutlined/>*/}
        {/*    </Button>*/}
        {/*  </Dropdown>*/}
        {/*</Form.Item>*/}

        <div
          style={{
            marginTop: "5px",
            marginBottom: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span>Image</span>
          {!uploadedFile && formProps.initialValues?.image?.webp?.main && (
            <Image
              src={formProps.initialValues?.image?.webp?.main}
              width={200}
              style={{ marginTop: "10px" }}
            />
          )}
        </div>

        <Form.Item name="image">
          <Upload
            name="image"
            listType="picture"
            accept=".jpg"
            maxCount={1}
            beforeUpload={(file) => {
              if (file.type !== "image/jpeg" || !file.name.endsWith(".jpg")) {
                setIsFileFormatValid(false);
                return true; // Unexpectedly, this will show file in red color as an error
              }

              if (file.size > FILE_SIZE) {
                setIsFileSizeValid(false);
                return true; // Unexpectedly, this will show file in red color as an error
              }

              setUploadedFile(file);

              setIsFileSizeValid(true);
              setIsFileFormatValid(true);
              return false; // Unexpectedly, this will show file in gray color as a success
            }}
            onRemove={() => {
              setUploadedFile(null);
            }}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>

        <div
          style={{
            marginTop: "-10px",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span style={{ fontSize: "12px" }}>
            Only <b>.jpg</b> files are permitted for the image. (.jpeg, .png,
            .webp and any other extension <b>are NOT allowed</b>)
          </span>
          <span style={{ fontSize: "12px" }}>
            Maximum file size is <b>2MB</b>.
          </span>
        </div>

        <Form.Item>
          <Button
            icon={<SaveOutlined />}
            type="primary"
            size="large"
            htmlType="submit"
            style={{ float: "right" }}
          >
            Save
          </Button>
        </Form.Item>

      </Form>
    </Edit>
  );
};
